import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { EventCardActions } from "@/components/event-card-actions";
import { useI18n } from "@/lib/i18n";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useServerFn } from "@tanstack/react-start";
import { deleteOwnAccount } from "@/lib/account.functions";
import { Clock, CheckCircle2, XCircle, Sparkles, Ticket, Coins, Trash2, Calendar, MapPin, ArrowRight, Wine, QrCode } from "lucide-react";

interface UpcomingEvent {
  id: string;
  name: string;
  description: string | null;
  starts_at: string;
  venue: string | null;
  cover_image_url: string | null;
}

export const Route = createFileRoute("/dashboard")({
  head: () => ({
    meta: [{ title: "Hesabım — JUËN" }],
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const { user, profile, isAdmin, isWaiter, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [deleting, setDeleting] = useState(false);
  const [upcoming, setUpcoming] = useState<UpcomingEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const deleteAccountFn = useServerFn(deleteOwnAccount);

  useEffect(() => {
    if (profile?.membership_status !== "approved") return;
    void (async () => {
      const now = new Date().toISOString();
      const { data } = await supabase
        .from("events")
        .select("id, name, description, starts_at, venue, cover_image_url")
        .eq("status", "published")
        .or(`ends_at.gte.${now},and(ends_at.is.null,starts_at.gte.${now})`)
        .order("starts_at", { ascending: true });
      setUpcoming((data as UpcomingEvent[]) ?? []);
      setLoadingEvents(false);
    })();
  }, [profile?.membership_status]);

  const handleDelete = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    if (!accessToken) {
      toast.error(t("account.noSession"));
      return;
    }
    setDeleting(true);
    const res = await deleteAccountFn({ data: { accessToken } });
    setDeleting(false);
    if (!res.success) {
      toast.error(res.error || t("account.dashboard.deleteFailed"));
      return;
    }
    toast.success(t("account.deleted"));
    await signOut();
    void navigate({ to: "/" });
  };

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
    if (!loading && user && isWaiter && !isAdmin) void navigate({ to: "/harikaradmin/scan" });
  }, [loading, user, isWaiter, isAdmin, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
            {t("account.dashboard.greeting", { name: profile?.first_name || t("account.title") })}
          </h1>
          <p className="mt-2 text-muted-foreground">{t("account.dashboard.subtitle")}</p>
        </div>

        {/* Membership status card — only show if not approved */}
        {profile?.membership_status !== "approved" && (
          <>
            <StatusCard status={profile?.membership_status ?? "pending"} />
            <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/5 p-5">
              <h2 className="text-base font-semibold text-destructive">{t("account.dashboard.deleteTitle")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("account.dashboard.deleteDesc")}
              </p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="mt-3" disabled={deleting}>
                    <Trash2 className="mr-1.5 h-4 w-4" />
                    {deleting ? t("account.deleting") : t("account.deleteAccountButton")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("account.deleteConfirmTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("account.dashboard.deleteConfirmDesc")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("account.giveUp")}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {t("account.confirmDelete")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        )}

        {profile?.membership_status === "approved" && (
          <Link
            to="/points"
            className="mt-6 block rounded-2xl border border-border bg-gradient-to-br from-primary/10 to-accent/10 p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                <Coins className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold">{t("account.dashboard.balanceTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("account.dashboard.balanceDesc")}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">
                  {(profile?.points_balance ?? 0).toLocaleString(locale)}
                  <span className="ml-1 text-base font-medium text-muted-foreground">J</span>
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-xl bg-primary px-4 py-2.5 text-primary-foreground">
              <span className="text-sm font-semibold">{t("account.dashboard.topUp")}</span>
              <ArrowRight className="h-4 w-4" />
            </div>
          </Link>
        )}

        {profile?.membership_status === "approved" && (
          <Link
            to="/menu"
            className="mt-6 block rounded-2xl border border-border bg-card p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                <Wine className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{t("account.dashboard.menuTitle")}</h2>
                <p className="text-sm text-muted-foreground">
                  {t("account.dashboard.menuDesc")}
                </p>
              </div>
            </div>
          </Link>
        )}

        {profile?.membership_status === "approved" && (
          <Link
            to="/vouchers"
            className="mt-6 block rounded-2xl border border-border bg-card p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                <QrCode className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{t("account.dashboard.vouchersTitle")}</h2>
                <p className="text-sm text-muted-foreground">
                  {t("account.dashboard.vouchersDesc")}
                </p>
              </div>
            </div>
          </Link>
        )}

        {profile?.membership_status === "approved" && (
          <Link
            to="/tickets"
            className="mt-6 block rounded-2xl border border-border bg-card p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                <Ticket className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{t("account.dashboard.ticketsTitle")}</h2>
                <p className="text-sm text-muted-foreground">
                  {t("account.dashboard.ticketsDesc")}
                </p>
              </div>
            </div>
          </Link>
        )}

        {profile?.membership_status === "approved" && (
          <div className="mt-6">
            <div className="mb-3 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">{t("account.dashboard.upcomingEvents")}</h2>
            </div>

            {loadingEvents ? (
              <div className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
                {t("common.loading")}
              </div>
            ) : upcoming.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                {t("account.dashboard.noEvents")}
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {upcoming.map((ev) => (
                  <div key={ev.id} className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-card">

                    <Link
                      to="/events/$eventId"
                      params={{ eventId: ev.id }}
                      className="block aspect-[16/9] w-full overflow-hidden bg-muted"
                    >
                      {ev.cover_image_url ? (
                        <img src={ev.cover_image_url} alt={ev.name} className="h-full w-full object-cover transition-transform hover:scale-105" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-muted-foreground">JUËN</div>
                      )}
                    </Link>

                    <div className="p-5">
                      <h3 className="text-lg font-semibold">{ev.name}</h3>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(ev.starts_at).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })}
                        </span>
                        {ev.venue && (
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5" />
                            {ev.venue}
                          </span>
                        )}
                      </div>
                      {ev.description && (
                        <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{ev.description}</p>
                      )}
                      <div className="mt-4 flex items-center gap-2">
                        <Button asChild className="flex-1">
                          <Link to="/events/$eventId" params={{ eventId: ev.id }}>
                            <Ticket className="mr-1.5 h-4 w-4" />
                            {t("account.dashboard.buyTicket")}
                          </Link>
                        </Button>
                        <Button asChild variant="outline" size="icon">
                          <Link to="/events/$eventId" params={{ eventId: ev.id }} aria-label={t("account.dashboard.detail")}>
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </Button>
                        <EventCardActions event={ev} />

                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          <Link
            to="/profile"
            className="group block rounded-2xl border border-border bg-card p-5 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
          >
            <h3 className="font-semibold">{t("account.dashboard.profileTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("account.dashboard.profileDesc")}</p>
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ status }: { status: "pending" | "approved" | "rejected" }) {
  const { t } = useI18n();
  const config = {
    pending: {
      icon: Clock,
      title: t("account.status.pending.title"),
      desc: t("account.status.pending.desc"),
      bg: "bg-warning/10",
      iconColor: "text-warning",
      border: "border-warning/30",
    },
    approved: {
      icon: CheckCircle2,
      title: t("account.status.approved.title"),
      desc: t("account.status.approved.desc"),
      bg: "bg-success/10",
      iconColor: "text-success",
      border: "border-success/30",
    },
    rejected: {
      icon: XCircle,
      title: t("account.status.rejected.title"),
      desc: t("account.status.rejected.desc"),
      bg: "bg-destructive/10",
      iconColor: "text-destructive",
      border: "border-destructive/30",
    },
  }[status];

  const Icon = config.icon;
  return (
    <div className={`rounded-2xl border ${config.border} ${config.bg} p-6`}>
      <div className="flex items-start gap-4">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background ${config.iconColor}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold">{config.title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{config.desc}</p>
        </div>
      </div>
    </div>
  );
}
